import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { WindowResizeService } from '../../global/window-resize.service';
import { map, withLatestFrom } from 'rxjs/operators';

export interface ProfileTab {
  name: string;
  path: string;
}

export const PROFILE_TABS: ProfileTab[] = [
  {
    name: 'Details',
    path: 'details'
  },
  {
    name: 'Preferences',
    path: 'preferences'
  },
  {
    name: 'Data Tables',
    path: 'data-tables'
  },
  {
    name: 'Reports',
    path: 'reports'
  },
  {
    name: 'Notifications',
    path: 'notifications'
  }
];

@Component({
  standalone: false,
  selector: 'dsp-profile',
  templateUrl: 'profile.container.html',
  styleUrls: ['profile.container.scss']
})
export class ProfileContainerComponent implements OnInit, OnDestroy {
  public tabs$: Observable<ProfileTab[]>;

  public selectedTabIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly subscription: Subscription = new Subscription();

  public constructor(
    private readonly router: Router,
    private readonly resize: WindowResizeService
  ) {
    this.tabs$ = this.selectTabs();
  }

  public ngOnInit(): void {
    /**
     * Check if user is being redirected from notifications
     */
    if (this.router.url.indexOf('notifications') > 0) {
      /**
       * Replace with hardcoded notifications index if any more tabs are added
       */
      this.selectedTabIndex$.next(PROFILE_TABS.length - 1);
    }

    this.subscription.add(this.subscribeIsMobile());
    this.subscription.add(this.subscribeTabChanges());
  }

  private subscribeTabChanges(): Subscription {
    return this.router.events.pipe(
      withLatestFrom(this.tabs$)
    ).subscribe(([event, tabs]) => {
      if (event instanceof NavigationEnd) {
        let tab;
        if (event.urlAfterRedirects.indexOf('?details=') > -1) {
          tab = tabs.find((currentTab) => currentTab.path === 'preferences') || '';
        } else {
          tab = tabs.find((currentTab) => event.urlAfterRedirects.indexOf(currentTab.path) > -1) || '';
        }
        const tabNames = [];
        tabs.forEach(record => {
          tabNames.push(record.name);
        });
        const index = tabNames.indexOf(tab.name);
        if (index > -1) {
          this.selectedTabIndex$.next(index);
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public changeTab(event: number): void {
    this.router.navigate(['/profile/' + PROFILE_TABS[event].path]);
  }

  private selectTabs(): Observable<ProfileTab[]> {
    return this.isMobile$.pipe(
      map(isMobile => isMobile ? PROFILE_TABS.filter(tab => tab.path === 'details') : PROFILE_TABS)
    );
  }

  private subscribeIsMobile(): Subscription {
    return this.resize.mobile$.subscribe(this.isMobile$);
  }
}
